import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { createDiscount } from "@/services/discountService";
import { resetAmountOffProducts } from "@/store/slices/discount.slice";
import SaveDiscountCouponSection from "../saveDiscountCouponSection";
import {
  AmountOffProducts,
  AmountOffProductsReqBody,
  PageType,
} from "@/types/coupon/discount";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { convertCurrencyToDecimal } from "@/utils/currency";

// UUID validation utility
const isValidUUID = (id?: string) =>
  typeof id === "string" &&
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
    id,
  );

const SaveAmountOffProducts = ({ currentPage }: { currentPage: PageType }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const [loading, setLoading] = useState(false);

  const { couponId, ...rest } = useAppSelector(
    (state) => state.discount[currentPage],
  ) as AmountOffProducts & { couponId?: string };
  // Destructure all required fields from rest
  const {
    title,
    discount_code,
    discount_type,
    discount_value_percentage,
    discount_value_fixed,
    end_date,
    minimum_purchase_type,
    start_date,
    applicable_products_type,
    applicable_collections,
    applicable_products,
    minimum_purchase_value_amount,
    minimum_purchase_value_quantity,
    discount_value_max,
  } = rest;

  const handleSave = async () => {
    // Edge case: Ensure title and discountCode are not empty
    const adjustedTitle = title || discount_code;
    const adjustedDiscountCode = discount_code || title;

    const validCouponId = isValidUUID(couponId) ? couponId : undefined;
    const reqBody: AmountOffProductsReqBody = {
      title: adjustedTitle,
      automatic: false,
      discountCode: adjustedDiscountCode,
      discountType: discount_type,
      discountValue: Number(
        discount_type === "percentage"
          ? discount_value_percentage
          : convertCurrencyToDecimal(Number(discount_value_fixed)),
      ),
      discountValueMax: Number(discount_value_max) > 0 ? convertCurrencyToDecimal(Number(discount_value_max)) : undefined,
      minimumPurchaseType: minimum_purchase_type,
      minimumPurchaseValue: Number(
        minimum_purchase_type === "amount"
          ? convertCurrencyToDecimal(Number(minimum_purchase_value_amount))
          : minimum_purchase_type === "quantity"
            ? minimum_purchase_value_quantity
            : 0,
      ),
      startDate: start_date ? new Date(start_date).getTime() : null,
      endDate: end_date ? new Date(end_date).getTime() : null,
      applicableProductsType: applicable_products_type,
      applicableProducts:
        applicable_products_type === "specificCollections"
          ? applicable_collections.map((collection) => collection.id)
          : applicable_products.map((product) => product.id),
      ...(validCouponId && { couponId: validCouponId }),
    };

    const endpoint = validCouponId
      ? `amount-off-products?id=${validCouponId}`
      : `amount-off-products`;
    // Log UUID and endpoint
    const vConsole = typeof window !== "undefined" && (window as any).vConsole;
    if (vConsole) {
      vConsole.log("[Coupon Save] UUID:", validCouponId);
      vConsole.log("[Coupon Save] Endpoint:", endpoint);
      vConsole.log("[Coupon Save] Request Body:", reqBody);
    } else {
      console.log("[Coupon Save] UUID:", validCouponId);
      console.log("[Coupon Save] Endpoint:", endpoint);
      console.log("[Coupon Save] Request Body:", reqBody);
    }

    try {
      setLoading(true);
      const response = await createDiscount({
        discountSlug: endpoint,
        data: reqBody,
      });
      if (vConsole) {
        vConsole.log("[Coupon Save] Response:", response);
      } else {
        console.log("[Coupon Save] Response:", response);
      }
      dispatch(resetAmountOffProducts());
      toast.success("Coupon created successfully");
      navigate(`/${storeType}/${storeId}/coupons`);
    } catch (error) {
      console.error("Create discount failed:", error);
      toast.error("Failed to create coupon");
    } finally {
      setLoading(false);
    }
  };

  return <SaveDiscountCouponSection loading={loading} onSave={handleSave} />;
};

export default SaveAmountOffProducts;
